import { QA_STATUSES, type QAFeedbackList, type QAEntityType } from '@intone/shared';

import { getStatusBgColor, getCategoryColor } from '@/shared/lib/colors';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQAFeedbackSummary } from '@/entities/qa/qaFeedback';

const STATUS_ORDER: Record<string, number> = {
  [QA_STATUSES.CRITICAL]: 0,
  [QA_STATUSES.MEDIUM_PRIORITY]: 1,
  [QA_STATUSES.LOW_PRIORITY]: 2,
  [QA_STATUSES.NO_PRIORITY_ASSIGNED]: 3,
  [QA_STATUSES.NEED_INFORMATION]: 4,
  [QA_STATUSES.REVIEWED]: 5,
  [QA_STATUSES.RESOLVED]: 6,
};

const formatData = (data: QAFeedbackList[] ) => {
  const statusCounts: Record<string, number> = {};
  const categoryCounts: Record<string, number> = {};

  for (const { status, category } of data) {
    statusCounts[status] = (statusCounts[status] || 0) + 1;
    categoryCounts[category] = (categoryCounts[category] || 0) + 1;
  }

  return {
    byStatus: Object.entries(statusCounts).sort(
      ([a], [b]) => (STATUS_ORDER[a] ?? 999) - (STATUS_ORDER[b] ?? 999)
    ),
    byCategory: Object.entries(categoryCounts).sort(
      ([a], [b]) => a.localeCompare(b)
    ),
  };
}

export function QASummaryStats({ entityType }: { entityType?: QAEntityType }) {
  const { data } = useQAFeedbackSummary(entityType);

  const weekStats =  formatData(data?.feedbacks || []);

  const totalCount =  data?.totalCount || 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Status Chart */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-body font-medium">
              Reports by Status (Last 7 days): {totalCount}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {weekStats.byStatus.length > 0 ? (
            <div className="space-y-3">
              {/* Horizontal stacked bar chart */}
              <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden flex">
                {weekStats.byStatus.map(([status, count]) => {
                  const percentage = totalCount > 0 ? (count /totalCount) * 100 : 0
                  return (
                    <div
                      key={status}
                      className={`h-full flex items-center justify-center text-detail font-medium text-white transition-all ${getStatusBgColor(status)}`}
                      style={{ width: `${percentage}%` }}
                      title={`${status}: ${count}`}
                    >
                      {percentage > 10 && count}
                    </div>
                  )
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {weekStats.byStatus.map(([status, count]) => (
                  <div key={status} className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded-sm ${getStatusBgColor(status)}`} />
                    <span className="text-detail text-gray-600">
                      {status} ({count})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-body text-gray-500">No issues this week</div>
          )}
        </CardContent>
      </Card>

      {/* Issue Type Chart */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-body font-medium">
              Reports by Issue Type (Last 7 days): {totalCount}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {weekStats.byCategory.length > 0 ? (
            <div className="space-y-3">
              {/* Horizontal stacked bar chart */}
              <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden flex">
                {weekStats.byCategory.map(([category, count], index) => {
                  const percentage = totalCount > 0 ? (count / totalCount) * 100 : 0
                  return (
                    <div
                      key={category}
                      className={`h-full flex items-center justify-center text-detail font-medium text-white transition-all ${getCategoryColor(index)}`}
                      style={{ width: `${percentage}%` }}
                      title={`${category}: ${count}`}
                    >
                      {percentage > 10 && count}
                    </div>
                  )
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {weekStats.byCategory.map(([category, count], index) => (
                  <div key={category} className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded-sm ${getCategoryColor(index)}`} />
                    <span className="text-detail text-gray-600">
                      {category} ({count})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-body text-gray-500">No issues this week</div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}